import React from 'react';
import { useReadContract } from 'wagmi';
import { LILVERSE_MINT_ADDRESS, LILVERSE_MINT_ABI } from '../../web3/lilverseMintContract';
import { activeChain } from '../../web3/chains';

// Polled rather than event-subscribed: the public RPC does not reliably support
// log subscriptions, and a 20s poll is well inside the resolution anyone needs
// from a supply counter.
const POLL_MS = 20000;

const MintProgress = () => {
    const { data: minted, isError: mintedError } = useReadContract({
        address: LILVERSE_MINT_ADDRESS,
        abi: LILVERSE_MINT_ABI,
        functionName: 'totalMinted',
        chainId: activeChain.id,
        query: { refetchInterval: POLL_MS },
    });

    const { data: supply, isError: supplyError } = useReadContract({
        address: LILVERSE_MINT_ADDRESS,
        abi: LILVERSE_MINT_ABI,
        functionName: 'MAX_SUPPLY',
        chainId: activeChain.id,
    });

    // Render nothing rather than an empty bar. A 0% bar on the homepage would
    // read as "nobody has minted", which is worse than showing no bar at all --
    // and that is exactly what a failed RPC read would otherwise produce.
    if (mintedError || supplyError) return null;
    if (minted === undefined || supply === undefined) return null;

    const mintedNum = Number(minted);
    const supplyNum = Number(supply);
    if (!supplyNum) return null;

    const pct = Math.min(100, (mintedNum / supplyNum) * 100);
    // Once a single token exists the bar must be visibly non-empty; at 1/5000 a
    // true-to-scale fill (0.02%) would round away to nothing.
    const shownPct = mintedNum > 0 ? Math.max(pct, 1.5) : 0;
    const soldOut = mintedNum >= supplyNum;

    return (
        <div className="w-full max-w-[300px] sm:max-w-[430px] flex flex-col gap-2 animate-fade-in-up [animation-delay:220ms]">
            <div className="flex items-baseline justify-between gap-3 px-1">
                <span className="font-dang text-[#B8355F] text-lg sm:text-2xl -tracking-[0.5px] tabular-nums">
                    {mintedNum.toLocaleString()} / {supplyNum.toLocaleString()}
                </span>
                <span className="font-mons text-[#B8355F]/70 text-xs sm:text-sm uppercase tracking-wide tabular-nums">
                    {soldOut ? 'Sold out' : `${pct < 1 ? pct.toFixed(1) : Math.round(pct)}% minted`}
                </span>
            </div>

            <div
                className="w-full h-[22px] sm:h-[26px] rounded-full bg-white border-[3px] border-white overflow-hidden drop-shadow"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={supplyNum}
                aria-valuenow={mintedNum}
                aria-label={`${mintedNum} of ${supplyNum} Lilverse NFTs minted`}
            >
                <div className="w-full h-full rounded-full bg-[#FEEEDE] overflow-hidden">
                    <div
                        className="h-full rounded-full bg-[#FD62A6] transition-[width] duration-700 ease-out motion-reduce:transition-none"
                        style={{ width: `${shownPct}%` }}
                    />
                </div>
            </div>
        </div>
    );
};

export default MintProgress;
